import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Shield, TrendingUp, Sun, Moon, Navigation, Inbox, Sparkles, Repeat } from 'lucide-react';
import { ConnectionStatus } from './ConnectionStatus';
import { useTheme } from '../contexts/ThemeContext';
import './Navbar.css';

const NavIcon = ({ src, alt }) => (
  <img src={src} alt={alt} className="nav-icon-img" style={{ display: 'block', objectFit: 'contain' }} />
);

const API = process.env.REACT_APP_BACKEND_URL;

// Poll the match-request inbox to drive the navbar badge counter.
const useMatchInboxCount = (isDriver) => {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!isDriver) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/match/requests/incoming`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      const pending = (d.requests || []).filter(x => x.status === 'pending').length;
      setCount(pending);
    } catch (_) { /* swallow — badge is best-effort */ }
  }, [isDriver]);

  useEffect(() => {
    if (!isDriver) return undefined;
    fetchCount();
    const id = setInterval(fetchCount, 15000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [isDriver, fetchCount]);

  return count;
};

const Navbar = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;
  const isB2B = !!user?.org_id;

  // Chameleon: B2B users get Einsatzplan nav, standard users get Mitfahrzentrale nav
  const navItems = isB2B ? [
    { path: '/', icon: '/assets/icons/home.svg', label: t('b2b.einsatzplan', 'Einsatzplan') },
    { path: '/orb', icon: '/assets/icons/offer.svg', label: 'Orb (B2B)' },
    { path: '/my-rides', icon: '/assets/icons/my_rides.svg', label: t('b2b.myTour', 'Meine Tour') },
  ] : [
    { path: '/dashboard', icon: '/assets/icons/home.svg', label: t('nav.dashboard', 'Dashboard') },
    { path: '/search', icon: '/assets/icons/search.svg', label: t('nav.search', 'Suche') },
    { path: '/create-ride', icon: '/assets/icons/offer.svg', label: t('nav.offer', 'Fahrt anbieten') },
    { path: '/my-rides', icon: '/assets/icons/my_rides.svg', label: t('nav.myRides', 'Meine Fahrten') },
    { path: '/driver-online', icon: '/assets/icons/offer.svg', label: t('nav.driverOnline', 'Live') },
  ];

  // Driver earnings uses lucide icon (no SVG file)
  const isDriver = user?.role === 'driver' || user?.role === 'both' || user?.is_admin;
  const matchCount = useMatchInboxCount(isDriver);

  // Safety Console access: only visible if user's org has is_safety_enabled OR user is super-admin
  const canAccessSafety = user?.is_safety_enabled === true;

  return (
    <nav className="navbar" data-testid="main-navbar">
      <div className="navbar-container">
        <Link to="/dashboard" className="navbar-brand" data-testid="navbar-brand">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <span>VoiceRide</span>
        </Link>

        <div className="navbar-links" data-testid="navbar-links">
          {navItems.map(({ path, icon, label }) => (
            <Link
              key={path}
              to={path}
              className={`nav-link ${isActive(path) ? 'active' : ''}`}
              data-testid={`nav-${path.slice(1)}`}
            >
              <NavIcon src={icon} alt={label} />
              <span>{label}</span>
            </Link>
          ))}

          {isDriver && (
            <Link
              to="/matches"
              className={`nav-link ${isActive('/matches') ? 'active' : ''}`}
              data-testid="nav-matches"
              title={t('nav.matchInbox', 'Anfragen')}
            >
              <span className="nav-match-icon-wrap">
                <Inbox className="nav-icon-lucide" />
                {matchCount > 0 && (
                  <span className="nav-badge" data-testid="nav-matches-badge">
                    {matchCount > 99 ? '99+' : matchCount}
                  </span>
                )}
              </span>
              <span>{t('nav.matchInbox', 'Anfragen')}</span>
            </Link>
          )}

          <Link
            to="/create-commute"
            className={`nav-link ${isActive('/create-commute') ? 'active' : ''}`}
            data-testid="nav-create-commute"
            title={t('nav.commute', 'Pendler-Strecke')}
          >
            <Repeat className="nav-icon-lucide" />
            <span>{t('nav.commute', 'Pendler-Strecke')}</span>
          </Link>

          <Link
            to="/events"
            className={`nav-link ${isActive('/events') ? 'active' : ''}`}
            data-testid="nav-events"
            title={t('nav.events', 'Events')}
          >
            <Sparkles className="nav-icon-lucide" />
            <span>{t('nav.events', 'Events')}</span>
          </Link>

          {user?.is_admin && (
            <Link
              to="/admin"
              className={`nav-link admin ${isActive('/admin') ? 'active' : ''}`}
              data-testid="nav-admin"
            >
              <Shield className="nav-icon-lucide" />
              <span>Admin</span>
            </Link>
          )}

          {canAccessSafety && (
            <Link
              to="/safety-dashboard"
              className={`nav-link ${isActive('/safety-dashboard') ? 'active' : ''}`}
              data-testid="nav-safety-console"
            >
              <Shield className="nav-icon-lucide" style={{ color: '#22c55e' }} />
              <span>{t('nav.safetyConsole', 'Safety Console')}</span>
            </Link>
          )}

          {isDriver && (
            <Link
              to="/driver/earnings"
              className={`nav-link ${isActive('/driver/earnings') ? 'active' : ''}`}
              data-testid="nav-driver-earnings"
            >
              <TrendingUp className="nav-icon-lucide" />
              <span>{t('nav.earnings', 'Einnahmen')}</span>
            </Link>
          )}
        </div>

        <div className="navbar-user">
          <ConnectionStatus />
          <button
            onClick={toggleTheme}
            className="theme-toggle-btn"
            data-testid="theme-toggle"
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <Link to="/profile" className={`user-avatar ${isActive('/profile') ? 'active' : ''}`} data-testid="nav-profile">
            <NavIcon src="/assets/icons/profil.svg" alt="Profile" />
            <span className="user-name">{user?.name || 'Profil'}</span>
          </Link>

          <button onClick={onLogout} className="logout-button" data-testid="logout-button" title={t('nav.logout', 'Abmelden')}>
            <LogOut className="nav-logout-icon" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
